import { Plus, Trash2 } from 'lucide-react';
import {
  PACKAGE_CONSUMABLES_MAX,
  formatStockQuantity,
  isInvalidSizeText,
  stockUnitShort,
  type ConsumableDraft,
} from '@mustauto/utils';
import { VEHICLE_SIZES, VEHICLE_SIZE_LABELS } from '@mustauto/types';
import { useInventory } from '@/hooks/useInventory';

const INPUT = 'rounded-md border border-input bg-background px-3 py-2 text-sm';

/**
 * Receita de insumos do pacote (F2.6): item do estoque + quanto o serviço
 * gasta, na unidade do item. Baixa sozinha quando a OS conclui; o detailer
 * ajusta na OS quando gastou diferente.
 */
export function PackageConsumablesField({
  value,
  onChange,
}: {
  value: ConsumableDraft[];
  onChange: (next: ConsumableDraft[]) => void;
}) {
  const inventory = useInventory();
  const items = inventory.items;
  const byId = new Map(items.map((i) => [i.id, i]));
  const used = new Set(value.map((v) => v.inventoryItemId));

  const update = (idx: number, patch: Partial<ConsumableDraft>) =>
    onChange(value.map((row, i) => (i === idx ? { ...row, ...patch } : row)));

  return (
    <fieldset className="space-y-2">
      <legend className="text-xs font-medium text-muted-foreground">Insumos do estoque — baixam sozinhos quando a OS conclui</legend>
      {inventory.isError ? (
        <p className="text-sm text-destructive">
          Não foi possível carregar o estoque.{' '}
          <button type="button" onClick={() => void inventory.refetch()} className="underline">
            Tentar de novo
          </button>
        </p>
      ) : inventory.isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando o estoque…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">Cadastre os insumos em Estoque (vitrificador em ml, cera em g) para montar a receita.</p>
      ) : (
        <>
          {value.map((row, idx) => {
            const item = byId.get(row.inventoryItemId);
            const unit = item ? stockUnitShort(item.unit) : '';
            return (
              <div key={idx} className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <select
                  aria-label="Item do estoque"
                  value={row.inventoryItemId}
                  // TROCAR de item é outra unidade: 30 ml de vitrificador não vira
                  // 30 g de cera — quantidade e portes recomeçam. A primeira
                  // escolha da linha mantém o que já foi digitado.
                  onChange={(e) =>
                    update(
                      idx,
                      row.inventoryItemId
                        ? { inventoryItemId: e.target.value, quantity: '', sizes: {}, showSizes: false }
                        : { inventoryItemId: e.target.value },
                    )
                  }
                  className={`${INPUT} min-w-0 flex-1`}
                >
                  <option value="">Escolha o insumo</option>
                  {items
                    .filter((i) => i.id === row.inventoryItemId || !used.has(i.id))
                    .map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.name} — {formatStockQuantity(i.quantity, i.unit)} em estoque
                      </option>
                    ))}
                </select>
                <input
                  aria-label="Quantidade por serviço"
                  type="number"
                  min={0.001}
                  step="any"
                  value={row.quantity}
                  onChange={(e) => update(idx, { quantity: e.target.value })}
                  placeholder="30"
                  className={`${INPUT} w-24`}
                />
                <span className="w-8 text-sm text-muted-foreground">{unit}</span>
                <button
                  type="button"
                  onClick={() => update(idx, { showSizes: !row.showSizes })}
                  aria-expanded={!!row.showSizes}
                  className="text-xs font-medium text-primary hover:underline"
                >
                  {row.showSizes ? 'Mesma para todo porte' : 'Por porte'}
                </button>
                <button
                  type="button"
                  onClick={() => onChange(value.filter((_, i) => i !== idx))}
                  aria-label="Remover insumo"
                  className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-destructive"
                >
                  <Trash2 size={16} />
                </button>
              </div>
              {row.showSizes && (
                // Quanto gasta em cada porte, quando difere da base — o SUV
                // gasta mais que o hatch. Vazio = a base; 0 = esse porte não usa.
                <div className="grid grid-cols-2 gap-2 pl-2 sm:grid-cols-4">
                  {VEHICLE_SIZES.map((size) => (
                    <label key={size} className="flex flex-col gap-1">
                      <span className="text-xs text-muted-foreground">{VEHICLE_SIZE_LABELS[size]}</span>
                      <span className="flex items-center gap-1">
                        <input
                          type="number"
                          min={0}
                          step="any"
                          value={row.sizes?.[size] ?? ''}
                          onChange={(e) => update(idx, { sizes: { ...row.sizes, [size]: e.target.value } })}
                          placeholder={row.quantity || '—'}
                          aria-invalid={isInvalidSizeText(row.sizes?.[size])}
                          className={`${INPUT} w-full`}
                        />
                        <span className="text-xs text-muted-foreground">{unit}</span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
              </div>
            );
          })}
          {value.length < PACKAGE_CONSUMABLES_MAX && (
            <button
              type="button"
              onClick={() => onChange([...value, { inventoryItemId: '', quantity: '' }])}
              className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
            >
              <Plus size={14} /> Adicionar insumo
            </button>
          )}
        </>
      )}
    </fieldset>
  );
}
